import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowLeft, Save, Eye, Upload, X, Image, Plus, Trash2, Building2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/utils/api';
import { toast } from 'sonner';

const CMSPageEditor = () => {
  const navigate = useNavigate();
  const { pageId } = useParams();
  const heroImageRef = useRef(null);
  const galleryImageRef = useRef(null);
  const logoImageRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageInfo, setPageInfo] = useState(null);
  const [newLogoName, setNewLogoName] = useState('');
  const [content, setContent] = useState({
    page_id: pageId,
    title: '',
    subtitle: '',
    hero_description: '',
    hero_image: '',
    sections: [],
    features: [],
    gallery_images: [],
    client_logos: []
  });

  const pageNames = {
    'about': 'About Us',
    'career-guidance': 'Career Guidance',
    'job-fairs': 'Job Fairs',
    'job-notification': 'Job Notification',
    'entrepreneurship': 'Entrepreneurship',
    'job-facilitation-center': 'Job Facilitation Center',
    'education-support': 'Education Support',
    'medical-support': 'Medical Support',
    'environment-disaster-support': 'Environment & Disaster Support',
    'our-clients': 'Our Clients',
  };

  const isOurClientsPage = pageId === 'our-clients';

  useEffect(() => {
    // Redirect achievements to dedicated CMS page
    if (pageId === 'achievements') {
      navigate('/admin/achievements-cms', { replace: true });
      return;
    }
    fetchPageContent();
  }, [pageId]);

  const fetchPageContent = async () => {
    try {
      const response = await api.get(`/cms/pages/${pageId}`);
      setContent(prev => ({
        ...prev,
        ...response.data
      }));
      setPageInfo({ id: pageId, name: pageNames[pageId] || pageId });
    } catch (error) {
      toast.error('Failed to fetch page content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/cms/pages/${pageId}`, content);
      toast.success('Page content saved successfully');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('page_id', pageId);
    formData.append('image_type', 'hero');

    try {
      const response = await api.post('/cms/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setContent(prev => ({ ...prev, hero_image: response.data.path }));
      toast.success('Hero image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const handleGalleryImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('page_id', pageId);
      formData.append('image_type', 'gallery');

      try {
        const response = await api.post('/cms/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setContent(prev => ({
          ...prev,
          gallery_images: [...(prev.gallery_images || []), response.data.path]
        }));
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    toast.success('Gallery images uploaded');
  };

  const removeGalleryImage = async (imageUrl, index) => {
    try {
      const formData = new FormData();
      formData.append('image_url', imageUrl);
      await api.delete('/cms/delete-image', { data: formData });
    } catch (error) {
      // Image might not exist on server, continue anyway
    }
    
    setContent(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
    toast.success('Image removed');
  };

  // Client Logo Functions
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!newLogoName.trim()) {
      toast.error('Please enter a client name first');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_name', newLogoName.trim());

    try {
      const response = await api.post('/cms/upload-client-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setContent(prev => ({
        ...prev,
        client_logos: [...(prev.client_logos || []), { name: response.data.company_name, logo: response.data.path }]
      }));
      setNewLogoName('');
      toast.success(`Logo for "${response.data.company_name}" uploaded`);
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  const removeClientLogo = (index) => {
    setContent(prev => ({
      ...prev,
      client_logos: prev.client_logos.filter((_, i) => i !== index)
    }));
    toast.success('Logo removed');
  };

  const updateClientLogoName = (index, newName) => {
    setContent(prev => ({
      ...prev,
      client_logos: prev.client_logos.map((logo, i) => 
        i === index ? { ...logo, name: newName } : logo
      )
    }));
  };

  const addSection = () => {
    setContent(prev => ({
      ...prev,
      sections: [...(prev.sections || []), { title: '', content: '' }]
    }));
  };

  const updateSection = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      sections: prev.sections.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const removeSection = (index) => {
    setContent(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const addFeature = () => {
    setContent(prev => ({
      ...prev,
      features: [...(prev.features || []), { title: '', description: '' }]
    }));
  };

  const updateFeature = (index, field, value) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
  };

  const removeFeature = (index) => {
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
        <p className="mt-4 text-slate-600">Loading page content...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/cms')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>
              Edit: {pageInfo?.name}
            </h1>
            <p className="text-slate-600">/{pageId}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => window.open(`/${pageId}`, '_blank')}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Our Clients Page - Special Layout */}
      {isOurClientsPage ? (
        <div className="space-y-6">
          {/* Hero Section */}
          <Card className="p-6 border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Page Header</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Page Title</label>
                <Input
                  value={content.title}
                  onChange={(e) => setContent(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter page title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtitle</label>
                <Input
                  value={content.subtitle}
                  onChange={(e) => setContent(prev => ({ ...prev, subtitle: e.target.value }))}
                  placeholder="Enter subtitle"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <Textarea
                  value={content.hero_description}
                  onChange={(e) => setContent(prev => ({ ...prev, hero_description: e.target.value }))}
                  placeholder="Enter description"
                  rows={2}
                />
              </div>
            </div>
          </Card>

          {/* Client Logos Management */}
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Client Logos</h2>
                <p className="text-sm text-slate-500">{(content.client_logos || []).length} logos</p>
              </div>
            </div>

            {/* Add New Logo */}
            <div className="bg-teal-50 rounded-lg p-4 mb-6">
              <h3 className="font-medium text-slate-900 mb-3">Add New Client Logo</h3>
              <div className="flex gap-3 items-end">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Client Name</label>
                  <Input
                    value={newLogoName}
                    onChange={(e) => setNewLogoName(e.target.value)}
                    placeholder="Enter client/company name"
                  />
                </div>
                <input
                  type="file"
                  ref={logoImageRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                <Button 
                  onClick={() => {
                    if (!newLogoName.trim()) {
                      toast.error('Please enter a client name first');
                      return;
                    }
                    logoImageRef.current?.click();
                  }}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Logo
                </Button>
              </div>
            </div>

            {/* Logos Grid */}
            {(content.client_logos || []).length === 0 ? (
              <p className="text-slate-500 text-center py-8">No client logos added yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
                {content.client_logos.map((client, index) => (
                  <div 
                    key={index} 
                    className="relative group bg-white rounded-lg border border-slate-200 p-2 hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-square flex items-center justify-center bg-slate-50 rounded mb-2">
                      <img 
                        src={client.logo} 
                        alt={client.name}
                        className="w-full h-full object-contain p-1"
                        onError={(e) => { 
                          e.target.src = '/placeholder-logo.png';
                        }}
                      />
                    </div>
                    <Input
                      value={client.name}
                      onChange={(e) => updateClientLogoName(index, e.target.value)}
                      className="text-xs h-7 text-center"
                      placeholder="Name"
                    />
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 rounded-full"
                      onClick={() => removeClientLogo(index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      ) : (
        /* Standard Page Layout */
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero Section */}
            <Card className="p-6 border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Hero Section</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Page Title</label>
                  <Input
                    value={content.title}
                    onChange={(e) => setContent(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter page title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Subtitle</label>
                  <Input
                    value={content.subtitle}
                    onChange={(e) => setContent(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Enter subtitle"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <Textarea
                    value={content.hero_description}
                    onChange={(e) => setContent(prev => ({ ...prev, hero_description: e.target.value }))}
                    placeholder="Enter hero description"
                    rows={4}
                  />
                </div>
              </div>
            </Card>

            {/* Content Sections */}
            <Card className="p-6 border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Content Sections</h2>
                <Button variant="outline" size="sm" onClick={addSection}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Section
                </Button>
              </div>
              
              {(content.sections || []).length === 0 ? (
                <p className="text-slate-500 text-center py-8">No sections added yet. Click "Add Section" to start.</p>
              ) : (
                <div className="space-y-4">
                  {content.sections.map((section, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-600">Section {index + 1}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeSection(index)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        <Input
                          value={section.title}
                          onChange={(e) => updateSection(index, 'title', e.target.value)}
                          placeholder="Section title"
                        />
                        <Textarea
                          value={section.content}
                          onChange={(e) => updateSection(index, 'content', e.target.value)}
                          placeholder="Section content"
                          rows={3}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Features */}
            <Card className="p-6 border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Features</h2>
                <Button variant="outline" size="sm" onClick={addFeature}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Feature
                </Button>
              </div>
              
              {(content.features || []).length === 0 ? (
                <p className="text-slate-500 text-center py-8">No features added yet. Click "Add Feature" to start.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {content.features.map((feature, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-medium text-slate-600">Feature {index + 1}</span>
                        <Button variant="ghost" size="sm" onClick={() => removeFeature(index)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        <Input
                          value={feature.title}
                          onChange={(e) => updateFeature(index, 'title', e.target.value)}
                          placeholder="Feature title"
                        />
                        <Textarea
                          value={feature.description}
                          onChange={(e) => updateFeature(index, 'description', e.target.value)}
                          placeholder="Feature description"
                          rows={2}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar - Images */}
          <div className="space-y-6">
            {/* Hero Image */}
            <Card className="p-6 border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Hero Image</h2>
              <input
                type="file"
                ref={heroImageRef}
                onChange={handleHeroImageUpload}
                accept="image/*"
                className="hidden"
              />
              
              {content.hero_image ? (
                <div className="relative">
                  <img 
                    src={content.hero_image} 
                    alt="Hero" 
                    className="w-full h-40 object-cover rounded-lg"
                  />
                  <Button 
                    variant="destructive" 
                    size="sm" 
                    className="absolute top-2 right-2"
                    onClick={() => setContent(prev => ({ ...prev, hero_image: '' }))}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div 
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-teal-500 transition-colors"
                  onClick={() => heroImageRef.current?.click()}
                >
                  <Image className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-600">Click to upload hero image</p>
                </div>
              )}
              
              {content.hero_image && (
                <Button 
                  variant="outline" 
                  className="w-full mt-3"
                  onClick={() => heroImageRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Replace Image
                </Button>
              )}
            </Card>

            {/* Gallery Images */}
            <Card className="p-6 border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Gallery Images</h2>
                <span className="text-sm text-slate-500">{(content.gallery_images || []).length} images</span>
              </div>
              
              <input
                type="file"
                ref={galleryImageRef}
                onChange={handleGalleryImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />

              <div className="grid grid-cols-2 gap-2 mb-4">
                {(content.gallery_images || []).map((img, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={img} 
                      alt={`Gallery ${index + 1}`} 
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                      onClick={() => removeGalleryImage(img, index)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => galleryImageRef.current?.click()}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Gallery Images
              </Button>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default CMSPageEditor;
